import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAttributionParams } from "@/hooks/useAttributionParams";

interface DropdownItem {
  label: string;
  href: string;
  desc?: string;
}

interface NavGroup {
  label: string;
  megaMenu?: boolean;
  columns?: { title: string; items: DropdownItem[] }[];
  items?: DropdownItem[];
  href?: string;
}

const navGroups: NavGroup[] = [
  {
    label: "Emotional Support Animals",
    megaMenu: true,
    columns: [
      {
        title: "ESA Resources",
        items: [
          { label: "What Is an ESA?", href: "/", desc: "Learn about emotional support animals" },
          { label: "How to Get an ESA Letter", href: "/how-to-get-esa-letter", desc: "Step-by-step guide, online or in person" },
          { label: "Housing Rights & Your ESA", href: "/housing-rights-esa", desc: "Fair Housing Act coverage" },
          { label: "ESA Letter Cost", href: "/esa-letter-cost", desc: "Transparent pricing info" },
          { label: "No Risk Guarantee", href: "/no-risk-guarantee", desc: "100% money-back policy" },
          { label: "Renew ESA Letter", href: "/renew-esa-letter", desc: "Renew an expiring or expired letter" },
        ],
      },
      {
        title: "Guides & Help",
        // LIVE-PUBLIC-PAGES-...-001 — owner decision: "ESA Letter for Landlords"
        // (/esa-letter-for-landlord) is REMOVED from the public navigation. The
        // route and page are unchanged and still reachable from contextual
        // in-page links, Guides/related-content cards, the sitemap and search;
        // it is not redirected or de-indexed. "ESA Letters for Apartments"
        // (/esa-letter-for-apartments) takes its place here — the single general
        // ESA apartment page (the CA/TX/FL/NY variants are state-scoped and
        // /psd-letter-for-apartments is a different product).
        items: [
          { label: "ESA Letters for Apartments", href: "/esa-letter-for-apartments", desc: "Renting an apartment with an ESA" },
          { label: "Landlord Denied Your ESA?", href: "/landlord-denied-esa-letter", desc: "Your rights and next steps" },
          { label: "Best Online ESA Letter Services", href: "/best-online-esa-letter-service", desc: "How to choose a trusted provider" },
          { label: "ESA Travel & Housing Support", href: "/travel-anxiety-esa-letter", desc: "Travel anxiety & temporary stays" },
          { label: "2026 HUD ESA Guidelines", href: "/blog/2026-hud-esa-guidelines", desc: "What the 2026 update changed" },
        ],
      },
      {
        title: "Find Your State",
        items: [
          { label: "Explore ESA Letters in All States", href: "/explore-esa-letters-all-states" },
          { label: "California ESA Letters", href: "/esa-letter/california" },
          { label: "Texas ESA Letters", href: "/esa-letter/texas" },
          { label: "Florida ESA Letters", href: "/esa-letter/florida" },
          { label: "New York ESA Letters", href: "/esa-letter/new-york" },
          { label: "Arizona ESA Letters", href: "/esa-letter/arizona" },
        ],
      },
    ],
  },
  {
    label: "Service Dogs",
    megaMenu: true,
    columns: [
      {
        title: "PSD Resources",
        items: [
          { label: "All About Service Dogs", href: "/all-about-service-dogs", desc: "Service dogs vs ESAs" },
          { label: "How to Get a PSD Letter", href: "/how-to-get-psd-letter", desc: "Psychiatric service dog letter guide" },
          { label: "Service Animal vs ESA", href: "/service-animal-vs-esa", desc: "Know the difference" },
        ],
      },
      {
        title: "PSD Letters by State",
        items: [
          { label: "All PSD states", href: "/explore-psd-letters-all-states" },
          { label: "California PSD Letter", href: "/psd-letter/california" },
          { label: "Texas PSD Letter", href: "/psd-letter/texas" },
          { label: "Florida PSD Letter", href: "/psd-letter/florida" },
          { label: "New York PSD Letter", href: "/psd-letter/new-york" },
          { label: "North Carolina PSD Letter", href: "/psd-letter/north-carolina" },
        ],
      },
    ],
  },
  {
    label: "Resources",
    megaMenu: true,
    columns: [
      {
        title: "Pet Policies",
        items: [
          { label: "College Pet Policy", href: "/college-pet-policy", desc: "ESA policies for 12+ top universities" },
          { label: "Airline Pet Policy", href: "/airline-pet-policy", desc: "Flying with your ESA in 2026" },
          { label: "Service Animal vs ESA", href: "/service-animal-vs-esa", desc: "Understand the differences" },
        ],
      },
      {
        title: "Learn",
        items: [
          { label: "Blog & Guides", href: "/blog", desc: "Expert articles & ESA resources" },
          { label: "FAQs", href: "/faqs", desc: "All your ESA questions answered" },
          { label: "Sitemap", href: "/sitemap", desc: "Browse all pages" },
        ],
      },
    ],
  },
  {
    label: "About",
    items: [
      { label: "About Us", href: "/about-us", desc: "Our mission & team" },
      { label: "FAQs", href: "/faqs", desc: "Common questions answered" },
      { label: "Contact Us", href: "/contact-us", desc: "Get in touch with us" },
      { label: "No Risk Guarantee", href: "/no-risk-guarantee", desc: "Our money-back promise" },
    ],
  },
];

export default function SharedNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const applyRef = useRef<HTMLDivElement>(null);
  const applyCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const { withAttribution } = useAttributionParams();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
      // Close mobile menu on scroll
      if (menuOpen) setMenuOpen(false);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
    setActiveDropdown(null);
    setMobileExpanded(null);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const handleMouseEnter = (label: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActiveDropdown(label);
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => setActiveDropdown(null), 120);
  };

  const handleApplyEnter = () => {
    if (applyCloseTimer.current) clearTimeout(applyCloseTimer.current);
    setApplyOpen(true);
  };

  const handleApplyLeave = () => {
    applyCloseTimer.current = setTimeout(() => setApplyOpen(false), 120);
  };

  const textColor = scrolled || !isHome ? "text-gray-700" : "text-white";
  const hoverColor = "hover:text-orange-500";

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled || !isHome ? "bg-white shadow-sm" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between" ref={dropdownRef}>
          {/* Logo — Phase 7 PageSpeed (2026-05-26): same WebP variants as
              the home Navbar (~7 KB / ~16 KB) replace the 111 KB / 4500×1576
              PNG. Legacy <img> fallback retained for browsers without WebP
              support. */}
          <Link to="/" className="flex items-center gap-2 cursor-pointer flex-shrink-0">
            <picture>
              <source
                type="image/webp"
                srcSet={
                  scrolled || !isHome
                    ? "/assets/brand/pawtenant-logo-black-320.webp 1x, /assets/brand/pawtenant-logo-black-640.webp 2x"
                    : "/assets/brand/pawtenant-logo-white-320.webp 1x, /assets/brand/pawtenant-logo-white-640.webp 2x"
                }
              />
              <img
                src={
                  scrolled || !isHome
                    ? "/assets/brand/pawtenant-logo-black-02.png"
                    : "/assets/brand/pawtenant-logo-white-02.png"
                }
                alt="PawTenant"
                width={400}
                height={160}
                decoding="async"
                className={`h-10 sm:h-14 w-auto object-contain transition-all ${!scrolled && isHome ? "brightness-0 invert" : ""}`}
              />
            </picture>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navGroups.map((group) => (
              <div
                key={group.label}
                className="relative"
                onMouseEnter={() => handleMouseEnter(group.label)}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  className={`flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors ${textColor} ${hoverColor} whitespace-nowrap cursor-pointer rounded-md hover:bg-black/5`}
                >
                  {group.label}
                  <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${activeDropdown === group.label ? "rotate-180" : ""}`}></i>
                </button>

                {/* Mega Menu */}
                {group.megaMenu && activeDropdown === group.label && (
                  <div
                    className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-xl border border-gray-100 p-6"
                    style={{ minWidth: (group.columns?.length ?? 2) >= 3 ? 780 : 600 }}
                    onMouseEnter={() => handleMouseEnter(group.label)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <div
                      className="grid gap-8"
                      style={{ gridTemplateColumns: `repeat(${group.columns?.length ?? 2}, minmax(0, 1fr))` }}
                    >
                      {group.columns?.map((col) => (
                        <div key={col.title}>
                          <p className="text-xs font-bold uppercase tracking-widest text-orange-500 mb-3">{col.title}</p>
                          <ul className="space-y-1">
                            {col.items.map((item) => (
                              <li key={item.href}>
                                <Link
                                  to={item.href}
                                  className="group flex flex-col px-3 py-2 rounded-lg hover:bg-orange-50 transition-colors cursor-pointer"
                                >
                                  <span className="text-sm font-medium text-gray-800 group-hover:text-orange-600">{item.label}</span>
                                  {item.desc && <span className="text-xs text-gray-400 mt-0.5">{item.desc}</span>}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Regular Dropdown */}
                {!group.megaMenu && activeDropdown === group.label && group.items && (
                  <div
                    className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-xl border border-gray-100 py-2 min-w-[220px]"
                    onMouseEnter={() => handleMouseEnter(group.label)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {group.items.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        className="group flex flex-col px-4 py-2.5 hover:bg-orange-50 transition-colors cursor-pointer"
                      >
                        <span className="text-sm font-medium text-gray-800 group-hover:text-orange-600">{item.label}</span>
                        {item.desc && <span className="text-xs text-gray-400">{item.desc}</span>}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <div className="w-px h-5 bg-gray-300/50 mx-2"></div>

            <a
              href="tel:+14099655885"
              className={`text-sm font-medium flex items-center gap-1 transition-colors ${hoverColor} ${textColor} whitespace-nowrap px-2`}
            >
              <i className="ri-phone-line text-xs"></i>
              (409) 965-5885
            </a>

            <Link
              to="/join-our-network"
              className={`whitespace-nowrap text-sm font-medium transition-colors px-3 py-2 rounded-md hover:bg-black/5 ${textColor} ${hoverColor}`}
            >
              For Therapists
            </Link>

            {/* Apply Now with ESA/PSD dropdown */}
            <div
              ref={applyRef}
              className="relative ml-1"
              onMouseEnter={handleApplyEnter}
              onMouseLeave={handleApplyLeave}
            >
              <button
                type="button"
                className="whitespace-nowrap flex items-center gap-1.5 px-5 py-2 bg-orange-500 text-white text-sm font-semibold rounded-md hover:bg-orange-600 transition-colors cursor-pointer"
              >
                Apply Now
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${applyOpen ? "rotate-180" : ""}`}></i>
              </button>

              {applyOpen && (
                <div
                  className="absolute top-full right-0 mt-1 bg-white rounded-xl border border-gray-100 py-1.5 min-w-[220px] z-50"
                  onMouseEnter={handleApplyEnter}
                  onMouseLeave={handleApplyLeave}
                >
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-4 pt-1.5 pb-1">I&apos;m applying for...</p>
                  <Link
                    to={withAttribution("/assessment")}
                    className="flex items-start gap-3 px-4 py-2.5 hover:bg-orange-50 transition-colors cursor-pointer group"
                    onClick={() => setApplyOpen(false)}
                  >
                    <div className="w-8 h-8 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0 mt-0.5">
                      <i className="ri-heart-line text-orange-500 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800 group-hover:text-orange-600">ESA Letter</p>
                      <p className="text-xs text-gray-400 leading-tight">Emotional Support Animal — housing rights</p>
                    </div>
                  </Link>
                  <Link
                    to={withAttribution("/psd-assessment")}
                    className="flex items-start gap-3 px-4 py-2.5 hover:bg-amber-50 transition-colors cursor-pointer group"
                    onClick={() => setApplyOpen(false)}
                  >
                    <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0 mt-0.5">
                      <i className="ri-service-line text-amber-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800 group-hover:text-amber-700">PSD Letter</p>
                      <p className="text-xs text-gray-400 leading-tight">Psychiatric Service Dog — different from an ESA</p>
                    </div>
                  </Link>
                </div>
              )}
            </div>

            <Link to="/customer-login" className="whitespace-nowrap flex items-center gap-1.5 text-sm text-gray-600 hover:text-orange-500 transition-colors cursor-pointer">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-user-line text-orange-400"></i>
              </div>
              My Orders
            </Link>
          </div>

          {/* Mobile right side: CTA + hamburger */}
          <div className="lg:hidden flex items-center gap-2">
            <Link
              to={withAttribution("/assessment")}
              className="whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 text-white text-xs font-bold rounded-lg hover:bg-orange-600 transition-colors cursor-pointer"
            >
              <i className="ri-heart-line text-xs"></i>
              Apply Now
            </Link>
            <button
              className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer transition-colors hover:bg-black/5"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
            >
              <i className={`text-xl ${scrolled || !isHome ? "text-gray-800" : "text-white"} ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Backdrop */}
      {menuOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile Menu Panel — slides in from right */}
      <div
        className={`fixed top-0 right-0 h-full w-[85vw] max-w-[340px] bg-white z-50 lg:hidden flex flex-col transition-transform duration-300 ease-in-out ${
          menuOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ boxShadow: menuOpen ? "-4px 0 24px rgba(0,0,0,0.12)" : "none" }}
      >
        {/* Panel header — Phase 7 PageSpeed: WebP replaces 115 KB PNG. */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <picture>
            <source
              type="image/webp"
              srcSet="/assets/brand/pawtenant-logo-black-320.webp 1x, /assets/brand/pawtenant-logo-black-640.webp 2x"
            />
            <img
              src="/assets/brand/pawtenant-logo-black-02.png"
              alt="PawTenant"
              width={400}
              height={160}
              loading="lazy"
              decoding="async"
              className="h-9 w-auto object-contain"
            />
          </picture>
          <button
            onClick={() => setMenuOpen(false)}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors cursor-pointer"
            aria-label="Close menu"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Scrollable nav links */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {/* Nav groups */}
          <div className="px-3 py-3 space-y-0.5">
            {navGroups.map((group) => (
              <div key={group.label} className="rounded-xl overflow-hidden">
                <button
                  className="w-full flex items-center justify-between px-3 py-3 text-sm font-semibold text-gray-800 hover:bg-orange-50 hover:text-orange-600 transition-colors cursor-pointer rounded-xl"
                  onClick={() => setMobileExpanded(mobileExpanded === group.label ? null : group.label)}
                >
                  <span>{group.label}</span>
                  <i className={`ri-arrow-down-s-line text-gray-400 transition-transform duration-200 ${mobileExpanded === group.label ? "rotate-180 text-orange-500" : ""}`}></i>
                </button>

                {mobileExpanded === group.label && (
                  <div className="pb-2 px-2">
                    {group.megaMenu
                      ? group.columns?.map((col) => (
                          <div key={col.title} className="mb-3">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-orange-500 px-2 py-1.5">{col.title}</p>
                            {col.items.map((item) => (
                              <Link
                                key={item.href}
                                to={item.href}
                                className="flex flex-col px-3 py-2 rounded-lg hover:bg-orange-50 transition-colors cursor-pointer"
                                onClick={() => setMenuOpen(false)}
                              >
                                <span className="text-sm font-medium text-gray-700">{item.label}</span>
                                {item.desc && <span className="text-xs text-gray-400 mt-0.5">{item.desc}</span>}
                              </Link>
                            ))}
                          </div>
                        ))
                      : group.items?.map((item) => (
                          <Link
                            key={item.href}
                            to={item.href}
                            className="flex flex-col px-3 py-2 rounded-lg hover:bg-orange-50 transition-colors cursor-pointer"
                            onClick={() => setMenuOpen(false)}
                          >
                            <span className="text-sm font-medium text-gray-700">{item.label}</span>
                            {item.desc && <span className="text-xs text-gray-400 mt-0.5">{item.desc}</span>}
                          </Link>
                        ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className="mx-4 border-t border-gray-100 my-2"></div>

          {/* Quick links */}
          <div className="px-3 pb-3 space-y-0.5">
            <a
              href="tel:+14099655885"
              className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors cursor-pointer"
            >
              <div className="w-8 h-8 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                <i className="ri-phone-line text-orange-500 text-sm"></i>
              </div>
              <div>
                <p className="font-semibold text-sm">Call Us</p>
                <p className="text-xs text-gray-400">(409) 965-5885</p>
              </div>
            </a>
            <Link
              to="/join-our-network"
              className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors cursor-pointer"
              onClick={() => setMenuOpen(false)}
            >
              <div className="w-8 h-8 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                <i className="ri-stethoscope-line text-orange-500 text-sm"></i>
              </div>
              <div>
                <p className="font-semibold text-sm">For Therapists</p>
                <p className="text-xs text-gray-400">Join our provider network</p>
              </div>
            </Link>
            <Link
              to="/customer-login"
              className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors cursor-pointer"
              onClick={() => setMenuOpen(false)}
            >
              <div className="w-8 h-8 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                <i className="ri-user-line text-orange-500 text-sm"></i>
              </div>
              <div>
                <p className="font-semibold text-sm">My Orders</p>
                <p className="text-xs text-gray-400">Track your ESA letter</p>
              </div>
            </Link>
          </div>
        </div>

        {/* Bottom CTA buttons — always visible */}
        <div className="flex-shrink-0 px-4 py-4 border-t border-gray-100 space-y-2.5 bg-white">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center mb-1">Apply For Your Letter</p>
          <Link
            to={withAttribution("/assessment")}
            className="whitespace-nowrap flex items-center justify-center gap-2 w-full px-4 py-3 bg-orange-500 text-white text-sm font-bold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer"
            onClick={() => setMenuOpen(false)}
          >
            <i className="ri-heart-line"></i>
            ESA Letter — Emotional Support Animal
          </Link>
          <Link
            to={withAttribution("/psd-assessment")}
            className="whitespace-nowrap flex items-center justify-center gap-2 w-full px-4 py-3 bg-amber-500 text-white text-sm font-bold rounded-xl hover:bg-amber-600 transition-colors cursor-pointer"
            onClick={() => setMenuOpen(false)}
          >
            <i className="ri-service-line"></i>
            PSD Letter — Psychiatric Service Dog
          </Link>
        </div>
      </div>
    </>
  );
}
